'use client';

import type { ReactNode } from 'react';
import type { ManualId } from 'src/types/manual/manual-domain';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { PageZones } from '../page-zones';
import { PageHeading } from '../page-heading/page-heading';

/**
 * 허브 한 장의 **껍데기** — 머리(구역·메뉴 이름 두 줄 · 주요 단추)와 조건 줄, 그리고 본문.
 *
 * 🔑 머리 이름은 사이드바가 쥔다(`PageHeading`) — `title` 은 사이드바가 없는 자리(공유 페이지)·
 *   메뉴 밖 화면이나, 고른 세트 이름처럼 메뉴 이름 대신 세울 때만 준다.
 * ⚠️ 제목 아래 설명 줄은 두지 않는다 — 화면이 무엇인지는 제목과 본문이 말한다.
 *
 * 🔑 세로 사이는 **골격(`PageZones`)이 준다** — 머리·`toolbar` 의 줄들이 상단 구역(`PAGE_GAP.top`),
 *   본문이 그 아래 한 단계 넓은 사이(`PAGE_GAP.zone`)에 선다. `toolbar` 는 조각(`<>…</>`)으로 넘기면
 *   줄마다 같은 사이를 받는다 — 안에서 `Stack spacing` 으로 다시 묶지 마라(그 줄만 사이가 달라진다).
 * ⛔ 머리와 본문 사이에 선을 긋지 않는다 — 가르는 것은 여백이다.
 * ⚠️ 본문은 이 껍데기가 그리지 않는다 — 허브마다 배치가 다르다(목록 한 칸 · 상세 두 칸).
 */
export function HubShell({
  title,
  actions,
  guide,
  toolbar,
  children,
}: {
  /** 메뉴 이름 대신 세울 제목 — 비면 사이드바의 메뉴 이름 */
  title?: string;
  /** 머리 오른쪽 — 추적·돌아가기 같은 주요 단추 */
  actions?: ReactNode;
  /** 이 화면의 매뉴얼 id — 주면 제목 줄 오른쪽 끝에 「사용 가이드」가 선다(자리는 `PageHeading`) */
  guide?: ManualId;
  /** 머리 아래 줄들 — 검색칸·조건 줄·띠(조각이면 줄마다 `PAGE_GAP.top`) */
  toolbar?: ReactNode;
  children: ReactNode;
}) {
  return (
    <PageZones
      top={(
        <>
          <PageHeading title={title} guide={guide} side={actions} />
          {toolbar}
        </>
      )}
    >
      {children}
    </PageZones>
  );
}

/**
 * 허브 본문의 **두 칸 배치** — 왼쪽은 읽을 것(추이·실물), 오른쪽은 견줄 것(요약·순위).
 * 좁은 화면에서는 한 칸으로 접힌다.
 *
 * `stickyRail` 은 오른쪽 칸을 **스크롤을 따라 붙인다** — 왼쪽이 훨씬 길 때 오른쪽이 위에 홀로 남지 않는다.
 * ⚠️ 붙일 칸은 칸 자체를 늘려야 한다(`alignSelf: stretch`) — 칸 높이가 내용만큼이면 붙을 자리가 없다.
 */
export function HubColumns({ main, rail, stickyRail = false }: { main: ReactNode; rail: ReactNode; stickyRail?: boolean }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' }, gap: 2, alignItems: 'start' }}>
      <Stack spacing={2} sx={{ minWidth: 0 }}>{main}</Stack>
      <Stack
        spacing={2}
        sx={{
          minWidth: 0,
          ...(stickyRail
            ? {
                alignSelf: { lg: 'stretch' },
                position: { lg: 'sticky' },
                top: { lg: 88 },
                maxHeight: { lg: 'calc(100vh - 110px)' },
                overflowY: { lg: 'auto' },
              }
            : {}),
        }}
      >
        {rail}
      </Stack>
    </Box>
  );
}
